import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS, HEADER_BADGE_CLASS } from '../components/shell/app-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Buildings, MagnifyingGlass } from '@phosphor-icons/react';
import { fetchAdminSchoolsPaginated, type SchoolDetailRow } from '../lib/admin-users';

const PAGE_SIZE = 15;
const HEADER_ICON = <Buildings size={26} />;

export default function AdminUsersSchools() {
  const navigate = useNavigate();
  const [schools, setSchools] = useState<SchoolDetailRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    setLoading(true);
    fetchAdminSchoolsPaginated(page, PAGE_SIZE)
      .then((r) => { setSchools(r.schools); setTotal(r.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  const filtered = useMemo(() => {
    if (!search.trim()) return schools;
    const q = search.toLowerCase();
    return schools.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.director_email?.toLowerCase().includes(q) ?? false),
    );
  }, [schools, search]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const onSearch = (v: string) => { setSearch(v); setPage(0); };

  return (
    <>
      <HeaderContent
        width="md"
        showBurger
        backTo="/admin/users"
        backLabel="Utilisateurs & Rôles"
        icon={HEADER_ICON}
        actions={
          <Button onClick={() => navigate('/admin/users/schools/new')}>
            Créer un établissement
          </Button>
        }
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <h1 className={HEADER_TITLE_CLASS}>Établissements</h1>
            <span className={HEADER_BADGE_CLASS}>Admin</span>
          </div>
          {!loading && (
            <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
              {total} établissement{total > 1 ? 's' : ''} enregistré{total > 1 ? 's' : ''}.
            </p>
          )}
        </div>
      </HeaderContent>
      <main className="max-w-[920px] mx-auto px-4 pb-16 pt-4">

        <div className="relative mb-4">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--naia-text-secondary)]" />
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Rechercher par nom ou email directeur…"
            className="pl-8 text-[13px]"
          />
        </div>

        {loading ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Chargement…</p>
        ) : filtered.length === 0 ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Aucun établissement{search ? ' trouvé' : ''}.</p>
        ) : (
          <>
            <ul className="sm:hidden list-none m-0 p-0 flex flex-col gap-3 mb-4">
              {filtered.map((s) => (
                <li
                  key={s.id}
                  data-testid="school-card"
                  className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] p-4 flex flex-col gap-2 min-w-0"
                >
                  <span className="font-bold text-[14px] text-[var(--naia-text-primary)] break-words">{s.name}</span>
                  <span className="flex items-start gap-1.5 text-[12px] text-[var(--naia-text-secondary)]">
                    <span className={`w-1.5 h-1.5 mt-1.5 rounded-full shrink-0 ${s.director_email ? 'bg-emerald-500' : 'bg-[var(--naia-text-secondary)] opacity-30'}`} />
                    <span className="break-all">{s.director_email ?? '—'}</span>
                  </span>
                  <span className="text-[12px] text-[var(--naia-text-secondary)]">
                    {s.teacher_count} enseignants · {s.class_count} classes · {s.active_student_count} / {s.student_count} élèves actifs
                  </span>
                  <Link
                    to={`/admin/users/schools/${s.id}`}
                    className="inline-flex items-center min-h-11 text-[13px] font-bold text-naia-blue hover:underline no-underline"
                  >
                    Voir →
                  </Link>
                </li>
              ))}
            </ul>
            <div className="hidden sm:block bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] overflow-hidden mb-4">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr className="border-b border-[var(--naia-line)] bg-[var(--naia-card-alt,var(--naia-card))]">
                      {['Établissement', 'Directeur', 'Enseignants', 'Classes', 'Élèves actifs', 'Créé le', ''].map((h) => (
                        <th key={h} className="text-left px-4 py-[10px] font-black text-[10.5px] tracking-[0.8px] text-[var(--naia-text-secondary)] uppercase whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--naia-line)]">
                    {filtered.map((s) => (
                      <tr key={s.id} className="hover:bg-[var(--naia-card-alt,rgba(0,0,0,0.02))] transition-colors">
                        <td className="px-4 py-3 font-bold text-[var(--naia-text-primary)]">{s.name}</td>
                        <td className="px-4 py-3 text-[var(--naia-text-secondary)]">
                          {s.director_email ? (
                            <span className="flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                              <span className="text-[12px]">{s.director_email}</span>
                            </span>
                          ) : (
                            <span className="flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-[var(--naia-text-secondary)] opacity-30 shrink-0" />
                              <span className="opacity-40 text-[12px]">—</span>
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 tabular-nums text-center font-extrabold text-naia-blue">{s.teacher_count}</td>
                        <td className="px-4 py-3 tabular-nums text-center font-extrabold text-[var(--naia-text-primary)]">{s.class_count}</td>
                        <td className="px-4 py-3 tabular-nums text-center">
                          <span className="font-extrabold text-[var(--naia-text-primary)]">{s.active_student_count}</span>
                          <span className="text-[11px] text-[var(--naia-text-secondary)]"> / {s.student_count}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-[var(--naia-text-secondary)] text-[12px]">
                          {new Date(s.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/admin/users/schools/${s.id}`}
                            className="inline-flex items-center min-h-11 min-w-11 text-[11.5px] font-bold text-naia-blue hover:underline no-underline whitespace-nowrap"
                          >
                            Voir →
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between text-[12.5px]">
                <span className="text-[var(--naia-text-secondary)]">
                  Page {page + 1} / {totalPages} — {total} établissement{total > 1 ? 's' : ''}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                    Précédent
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                    Suivant
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </>
  );
}
